import { useState } from 'react';

import { cn } from '@sandtech-hub-monorepo/ui-kit';

import { AboutSectionEditDialog } from '../Edit/AboutSectionEditDialog';
import { ReactComponent as EmptyAboutIcon } from '../../assets/emptyState/emptyAbout.svg';
import ProfileEmptySection from './ProfileEmptySection';
import type { EmptyStateStyling } from './ProfileEmptySection';
import { ProfileCard } from './ProfileCard';

interface EmptyStateContent {
  header?: string;
  main?: string;
  callToActionText?: string;
  icon?: JSX.Element;
}

interface Props {
  editable?: boolean;
  about?: string;
  defaultOpen?: boolean;
  /** Card heading; defaults to central-hub's "About me". FIT passes "About Me". */
  title?: string;
  /** When set, clamps the bio to a few lines behind a "Read more" toggle (FIT). */
  truncate?: boolean;
  /** Empty-state copy; defaults to central-hub's original. FIT passes its own (Figma 10708-66006). */
  emptyState?: EmptyStateContent;
  /** Empty-state visual overrides; omitted by central-hub. */
  emptyStateStyling?: EmptyStateStyling;
}

export const AboutSection = ({
  editable,
  about,
  defaultOpen = false,
  title = 'About me',
  truncate = false,
  emptyState,
  emptyStateStyling,
}: Props) => {
  const [editDialogOpen, setEditDialogOpen] = useState(defaultOpen);
  const [expanded, setExpanded] = useState(false);

  const showToggle = truncate && !!about && about.length > 240;

  return (
    <>
      <ProfileCard
        title={title}
        editable={editable}
        action={() => setEditDialogOpen(true)}
      >
        {about ? (
          <div className="space-y-2 whitespace-pre-wrap">
            <p className={cn(showToggle && !expanded && 'line-clamp-5')}>
              {about}
            </p>
            {showToggle && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-primary font-medium"
              >
                {expanded ? 'Read less' : 'Read more'}
              </button>
            )}
          </div>
        ) : (
          <ProfileEmptySection
            header={
              emptyState?.header ?? 'You seem like someone interesting...'
            }
            main={
              emptyState?.main ??
              'Tell us a little about you, your passion, what you live for...'
            }
            callToActionText={
              emptyState?.callToActionText ?? 'Add About Me Info'
            }
            callToAction={() => setEditDialogOpen(true)}
            icon={emptyState?.icon ?? <EmptyAboutIcon />}
            {...emptyStateStyling}
          />
        )}
      </ProfileCard>

      {editable && (
        <AboutSectionEditDialog
          title={title}
          currentValues={{ about }}
          open={editDialogOpen}
          handleOpenChange={(open) => setEditDialogOpen(open)}
        />
      )}
    </>
  );
};
